import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import UploadItemButton from '@wix/da-shared-react/pkg/UploadItemButton';
import NativeDropdown from '@wix/da-ds/pkg/Dropdown/NativeDropdown';
import SelectToggle from '@wix/da-shared-react/pkg/SelectToggle';
import { MIME_CONFIG } from '@wix/da-shared-react/pkg/utils/mime';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { useUnlockVideoUploadsCheck } from '../UnlockWithCoreUpsellModal/useUnlockVideoUploadsCheck';
import {
  GenericRequestStatusType,
  PresetsInitResponse,
} from '../../redux/types';
import FileTypeButton from './_partials/FileTypeButton';
import { SAVED_SUBMISSIONS_FOLDER_NAME } from '../../constants';

import s from './DeviationPopupDropZone.scss';

const STUDIO_UPLOADS_NAMESPACE = 'studio';

export interface Props {
  className?: string;
  tierId?: number;
  referer?: string;
  isPageLoadModal?: boolean;
  requestClose: () => void;
  initializeTierTags: (tierId: number, onError: () => void) => void;
  onDeviationCreated: (deviationId: number) => void;
  presetsStatus: GenericRequestStatusType;
  presetsInit?: PresetsInitResponse;
  presetsInitFetch: () => void;
  presetsSetActive: (presetId: number) => void;
}

const DeviationPopupDropZone: React.FC<Props> = ({
  className,
  tierId,
  referer,
  isPageLoadModal,
  requestClose,
  initializeTierTags,
  onDeviationCreated,
  presetsStatus,
  presetsInit,
  presetsInitFetch,
  presetsSetActive,
}) => {
  const isDesktop = !useContext(MobileContext);
  const { hasCoreMembership } = useContext(SessionContext);
  const showExtraButtons = useFlag('submit_extra_buttons');
  const presetsReady =
    presetsStatus !== 'uninitialized' && presetsInit !== undefined;

  const [dragOver, setDragOver] = useState(false);
  const isMultiUploadRef = useRef<boolean>(false);
  const { t } = useTranslation();

  const onSuccess = useCallback(
    ({ deviationId }) => {
      if (!isMultiUploadRef.current) {
        onDeviationCreated(deviationId);
      }
    },
    [onDeviationCreated]
  );

  const onDragChange = useCallback(isDragging => setDragOver(isDragging), []);

  const {
    modal: lockedVideoUpsellDialog,
    showModal: showLockedVideoUpsellModal,
    areVideoUploadsLocked,
    ...lockedVideoUploadOptions
  } = useUnlockVideoUploadsCheck();
  const onDrop = useCallback(
    files => {
      // If multiple files are dropped we close the modal to reveal studio page below
      if (files.length > 1) {
        isMultiUploadRef.current = true;
        requestClose();
      }
      return true;
    },
    [requestClose]
  );

  // Determine if we should redirect back to the referer when the modal is closed
  const redirectOnClose = useMemo(() => {
    const url =
      typeof window !== 'undefined' && new URL(window.location.toString());
    const stopRedirectToReferer =
      url &&
      url.searchParams &&
      url.searchParams.size === 1 &&
      url.searchParams.get('new');

    return isPageLoadModal && !stopRedirectToReferer;
  }, [isPageLoadModal]);

  const onModalClose = useCallback(() => {
    if (redirectOnClose && referer) {
      window.location.href = referer;
    } else {
      requestClose();
    }
  }, [redirectOnClose, referer, requestClose]);

  useEffect(() => {
    if (tierId) {
      initializeTierTags(tierId, () => {
        requestClose(); // on error
      });
    }
  }, [tierId, initializeTierTags, requestClose]);

  const [presetsIsOpen, setPresetsIsOpen] = useState<boolean>(false);
  const onSelectPreset = useCallback(
    presetId => {
      presetsSetActive(presetId);
      setPresetsIsOpen(false);
    },
    [presetsSetActive, setPresetsIsOpen]
  );

  useEffect(() => {
    if (!presetsReady) {
      presetsInitFetch();
    }
  }, [presetsReady, presetsInitFetch]);

  const commonUploadOptions = useMemo(
    () => ({
      id: STUDIO_UPLOADS_NAMESPACE,
      uploadType: UploadType.DEVIATION_NEW,
      uploadParams: {
        folderName: SAVED_SUBMISSIONS_FOLDER_NAME,
        useDefaults: true,
      },
      allowMultiple: true,
      onDrop,
      ...lockedVideoUploadOptions,
    }),
    [onDrop, lockedVideoUploadOptions]
  );

  let presetsSelector;
  if (presetsReady && presetsInit?.presets.length >= 2) {
    presetsSelector = isDesktop ? (
      <div className={s['presets']}>
        <span className={s['presets-label']}>
          {t('deviationSubmit.modals.presets.title')}
        </span>
        <SelectField
          className={s['presets-select']}
          id="presetId"
          label={t('deviationSubmit.modals.presets.title')}
          isLabelHidden
          noBottomSpacing
          withBlankOption={false}
          value={presetsInit?.presetId}
          items={presetsInit?.presets.map(preset => ({
            value: preset.presetId,
            label: preset.presetName || 'Untitled',
          }))}
          onSelect={onSelectPreset}
        />
      </div>
    ) : (
      <div className={s['presets']}>
        <SelectToggle
          label={t('deviationSubmit.modals.presets.title')}
          value={presetsInit?.presetName || 'Untitled'}
          isToggled={presetsIsOpen}
        />
        <div className={s['presets-dropdown-hidden']}>
          <NativeDropdown
            value={presetsInit?.presetId}
            items={presetsInit?.presets.map(preset => ({
              value: preset.presetId,
              label: preset.presetName || 'Untitled',
            }))}
            onClick={() => setPresetsIsOpen(!presetsIsOpen)}
            onSelect={onSelectPreset}
          />
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen
      isFullscreen
      heading={t(
        tierId
          ? 'deviationSubmit.title.createToTier'
          : 'deviationSubmit.title.create'
      )}
      headerRight={isDesktop && presetsSelector}
      subheading={!isDesktop && presetsSelector}
      layoutClassName={s['layout']}
      showHeaderBorder
      onRequestClose={onModalClose}
    >
      <div
        className={classnames(s['root'], className, dragOver && s['drag-over'])}
      >
        {lockedVideoUpsellDialog}
        <UploadItemButton
          uploadOptions={{
            ...commonUploadOptions,
            acceptedMimeTypes: MIME_CONFIG.forDeviationSubmit,
          }}
          className={s['upload-button']}
          dropZoneClassName={s['upload-dropzone']}
          ctaLabel={t('uploadZone.ready.cta')}
          subtitle={t(
            `uploadZone.ready.subtitle${hasCoreMembership ? '.core' : ''}${
              isDesktop ? '' : '.mobile'
            }`
          )}
          onSuccess={onSuccess}
          onDragChange={onDragChange}
          aboveCtaSlot={
            showExtraButtons && (
              <div className={s['file-type-buttons']}>
                <FileTypeButton
                  fileType="images"
                  uploadOptions={commonUploadOptions}
                  onSuccess={onSuccess}
                />
                <FileTypeButton
                  fileType="videos"
                  uploadOptions={commonUploadOptions}
                  onSuccess={onSuccess}
                  onLockedClick={
                    areVideoUploadsLocked
                      ? showLockedVideoUpsellModal
                      : undefined
                  }
                />
                <FileTypeButton
                  fileType="gifs"
                  uploadOptions={commonUploadOptions}
                  onSuccess={onSuccess}
                />
              </div>
            )
          }
          belowCtaSlot={
            <TextButton
              className={s['studio-link']}
              size="large"
              variant="main"
              onClick={ev => {
                ev.stopPropagation();
                requestClose();
              }}
            >
              {t('uploadZone.ready.studioLink')}
            </TextButton>
          }
        />
      </div>
    </Modal>
  );
};
DeviationPopupDropZone.displayName = 'DeviationPopupDropZone';

export default DeviationPopupDropZone;
